/**
 * ComplaintDialog
 *
 * Quick "log a complaint" form launched from the homepage. Built by
 * mirroring the LeadFormDialog shell — same dialog structure, same
 * photo uploader, same toast behaviour. Open to every logged-in user.
 * Admin/Manager see the full register in the admin panel; everyone sees
 * the complaints they logged in My Registrations > Complaints.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { api, API, useAuth } from '../lib/store';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Camera, X } from 'lucide-react';

const MAX_SITE_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const STATUSES = [
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'closed',      label: 'Closed' },
];

const buildInitial = (user) => ({
  customer_name: '',
  mobile: '',
  description: '',
  attended_by_id:   user?.id   || '',
  attended_by_name: user?.name || '',
  status: 'pending',
  action_taken: '',
  remarks: '',
});

export default function ComplaintDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => buildInitial(user));
  const [submitting, setSubmitting] = useState(false);
  const [salespeople, setSalespeople] = useState([]);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(buildInitial(user));
      setPendingPhotos([]);
      setPendingPreviews((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
    }
  }, [open, user]);

  useEffect(() => () => {
    pendingPreviews.forEach((u) => URL.revokeObjectURL(u));
  }, [pendingPreviews]);

  // Same endpoint the Reports tab / Lead form uses.
  useEffect(() => {
    if (!open || salespeople.length > 0) return;
    api.getSalespeople()
      .then((res) => setSalespeople(res.data?.salespeople || []))
      .catch(() => setSalespeople([]));
  }, [open, salespeople.length]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Digits-only input for the phone field — matches the requirement
  // ("mobile: digits only on input, stored as given").
  const onMobileChange = (e) => {
    const digits = (e.target.value || '').replace(/\D/g, '');
    setForm((f) => ({ ...f, mobile: digits }));
  };

  const handleAttendedByChange = (id) => {
    const sp = salespeople.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      attended_by_id: id,
      attended_by_name: sp?.name || '',
    }));
  };

  const handlePickPhotos = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length === 0) return;
    const remainingSlots = MAX_SITE_IMAGES - pendingPhotos.length;
    if (remainingSlots <= 0) {
      toast.error(`Max ${MAX_SITE_IMAGES} photos per complaint`);
      e.target.value = '';
      return;
    }
    const accepted = [];
    const previews = [];
    for (const file of chosen.slice(0, remainingSlots)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: not an image`);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error(`${file.name}: exceeds 5 MB`);
        continue;
      }
      accepted.push(file);
      previews.push(URL.createObjectURL(file));
    }
    if (chosen.length > remainingSlots) {
      toast.info(`Only kept ${remainingSlots} — cap is ${MAX_SITE_IMAGES}`);
    }
    setPendingPhotos((prev) => [...prev, ...accepted]);
    setPendingPreviews((prev) => [...prev, ...previews]);
    e.target.value = '';
  };

  const removePendingPhoto = (idx) => {
    setPendingPhotos((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    const mobileDigits = (form.mobile || '').replace(/\D/g, '');
    if (mobileDigits.length < 10) {
      toast.error('Customer mobile number is required (at least 10 digits)');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Complaint description is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.createComplaint(form);
      const created = res.data;
      if (pendingPhotos.length > 0) {
        try {
          const fd = new FormData();
          pendingPhotos.forEach((file) => fd.append('files', file, file.name));
          const token = localStorage.getItem('heda_token') || '';
          await axios.post(`${API}/complaints/${created.id}/images`, fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
        } catch (upErr) {
          toast.error(upErr.response?.data?.detail || 'Photos upload failed — complaint is saved');
        }
      }
      // Admins use Admin > Complaints; everyone else finds it in My Registrations.
      toast.success('Complaint saved', user?.role === 'admin' ? undefined : {
        action: { label: 'View', onClick: () => navigate('/my-registrations?tab=complaints') },
      });
      setForm(buildInitial(user));
      setPendingPhotos([]);
      setPendingPreviews((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
      onOpenChange(false);
      if (onCreated) onCreated(created);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save complaint');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-lg max-h-[92vh] flex flex-col" data-testid="complaint-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-700" /> Log a Complaint
          </DialogTitle>
          <p className="text-xs text-heda-sub mt-1">
            Only the customer name is required. Everything else is optional.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-1 space-y-3 [&_input]:min-h-11 sm:[&_input]:min-h-10">
          <div>
            <Label htmlFor="cf-name" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Customer Name <span className="text-red-700">*</span></Label>
            <Input className="rounded-xl bg-heda-cream/50"
              id="cf-name"
              value={form.customer_name}
              onChange={set('customer_name')}
              placeholder=""
              autoFocus
              required
              data-testid="complaint-form-name"
            />
          </div>

          <div>
            <Label htmlFor="cf-description" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Description <span className="text-red-700">*</span></Label>
            <Textarea className="rounded-xl bg-heda-cream/50"
              id="cf-description"
              value={form.description}
              onChange={set('description')}
              placeholder="What is the customer complaining about?"
              rows={4}
              required
              data-testid="complaint-form-description"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cf-mobile" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Mobile Number <span className="text-red-700">*</span></Label>
              <Input className="rounded-xl bg-heda-cream/50"
                id="cf-mobile"
                type="tel"
                inputMode="numeric"
                value={form.mobile}
                onChange={onMobileChange}
                placeholder=""
                required
                data-testid="complaint-form-mobile"
              />
            </div>
            <div>
              <Label htmlFor="cf-status" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Status</Label>
              <Select value={form.status} onValueChange={setSel('status')}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50" id="cf-status" data-testid="complaint-form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`complaint-form-status-option-${s.value}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="cf-attended" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Attended By</Label>
            <Select
              value={form.attended_by_id || undefined}
              onValueChange={handleAttendedByChange}
            >
              <SelectTrigger className="rounded-xl bg-heda-cream/50" id="cf-attended" data-testid="complaint-form-attended-by">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {salespeople.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`complaint-form-attended-option-${s.id}`}>
                    {s.name}{s.staff_code ? ` (${s.staff_code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="cf-action" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Action Taken <span className="text-heda-mute">(optional)</span></Label>
            <Textarea className="rounded-xl bg-heda-cream/50"
              id="cf-action"
              value={form.action_taken}
              onChange={set('action_taken')}
              placeholder=""
              rows={2}
              data-testid="complaint-form-action"
            />
          </div>

          <div>
            <Label htmlFor="cf-remarks" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Remarks <span className="text-heda-mute">(optional)</span></Label>
            <Textarea className="rounded-xl bg-heda-cream/50"
              id="cf-remarks"
              value={form.remarks}
              onChange={set('remarks')}
              placeholder=""
              rows={2}
              data-testid="complaint-form-remarks"
            />
          </div>

          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-red-700" />
              Photos <span className="text-heda-mute">(optional · up to {MAX_SITE_IMAGES})</span>
            </Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="complaint-form-photos">
              {pendingPreviews.map((url, idx) => (
                <div
                  key={url}
                  className="relative w-16 h-16 rounded-xl overflow-hidden border border-heda-line bg-heda-cream"
                  data-testid={`complaint-form-photo-preview-${idx}`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePendingPhoto(idx)}
                    className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Remove photo"
                    data-testid={`complaint-form-photo-remove-${idx}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {pendingPhotos.length < MAX_SITE_IMAGES && (
                <label
                  className="w-16 h-16 rounded-xl border border-dashed border-heda-line bg-heda-cream flex flex-col items-center justify-center cursor-pointer hover:bg-heda-cream/60"
                  data-testid="complaint-form-photo-add"
                >
                  <Camera className="w-4 h-4 text-heda-sub" />
                  <span className="text-[9px] text-heda-sub mt-0.5">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePickPhotos}
                    data-testid="complaint-form-photo-input"
                  />
                </label>
              )}
            </div>
            <p className="text-[9px] text-heda-mute mt-1">JPG / PNG / WEBP · max 5 MB each · uploaded after the complaint is saved.</p>
          </div>
        </form>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-heda-line gap-2 sm:gap-0">
          <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="complaint-form-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            className="rounded-xl bg-red-700 hover:bg-red-800 text-white"
            disabled={submitting}
            data-testid="complaint-form-submit"
          >
            {submitting ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Saving…</> : 'Save Complaint'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
